import type { ErrorEvent, EventHint } from "@sentry/react";
import Axios, { AxiosError, AxiosResponseHeaders, RawAxiosResponseHeaders } from "axios";

import { env } from "@/env";

// Sentry serializes an `Error` to `{ type, value, stacktrace }`, so an
// `AxiosError` reaches Sentry as just "Request failed with status code 400" —
// the backend's response body (the part that actually explains the failure)
// never leaves the browser, and every 4xx groups into a single issue.
//
// We fix this in two places:
//   1. The REST response interceptor stashes a sanitized, truncated copy of the
//      response body here (async, so it can read `Blob` bodies from the export
//      endpoints). See `stashAxiosErrorContext`.
//   2. `Sentry.init`'s `beforeSend` reads that stash and attaches it as an
//      `response` context, rewrites the issue title, and fingerprints by
//      endpoint + status so each endpoint/status pair is its own issue.
//
// The stash lives in a WeakMap keyed by the error object — never a property on
// the error — so it stays invisible to `extraErrorDataIntegration`,
// `console.error`, and React Query devtools, and is GC'd with the error.

type AxiosErrorContext = {
  method?: string;
  path?: string;
  status?: number;
  statusText?: string;
  requestId?: string;
  body?: string;
};

const axiosErrorContext = new WeakMap<AxiosError, AxiosErrorContext>();

const MAX_BODY_LENGTH = 4096;
const REDACTED = "[redacted]";

// Backend validation errors echo submitted values back in their messages, so a
// failed create can put credentials or PII into the body. With
// `sendDefaultPii: false` set as a deliberate stance, redact any key that looks
// sensitive before the body is attached.
const SENSITIVE_KEY_PATTERN =
  /pass(word)?|token|secret|authorization|apikey|api_key|email|phone|ssn|card|cvv|iban/i;

const truncate = (value: string): string =>
  value.length > MAX_BODY_LENGTH
    ? `${value.slice(0, MAX_BODY_LENGTH)}… [truncated ${value.length - MAX_BODY_LENGTH} chars]`
    : value;

const redactValue = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(redactValue);
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, val]) => [
        key,
        SENSITIVE_KEY_PATTERN.test(key) ? REDACTED : redactValue(val),
      ])
    );
  }
  return value;
};

const stringifyRedacted = (value: unknown): string => {
  try {
    return truncate(JSON.stringify(redactValue(value)));
  } catch {
    return truncate(String(value));
  }
};

const redactString = (raw: string): string => {
  const trimmed = raw.trim();
  if (trimmed.startsWith("{") || trimmed.startsWith("[")) {
    try {
      return stringifyRedacted(JSON.parse(trimmed));
    } catch {
      // Not JSON after all (e.g. an HTML error page) — fall through to raw.
    }
  }
  return truncate(raw);
};

const extractBody = async (data: unknown): Promise<string | undefined> => {
  if (data == null || data === "") return undefined;
  if (typeof data === "string") return redactString(data);
  if (typeof Blob !== "undefined" && data instanceof Blob) {
    return redactString(await data.text());
  }
  return stringifyRedacted(data);
};

const getRequestId = (
  headers: RawAxiosResponseHeaders | AxiosResponseHeaders | undefined
): string | undefined => {
  const value = headers?.["x-request-id"] ?? headers?.["x-requestid"];
  return value == null ? undefined : String(value);
};

/**
 * Stash a sanitized copy of an axios error's response for Sentry. Safe to call
 * unconditionally from the response interceptor — it never throws and no-ops
 * when Sentry is disabled or the status is 401 (expected auth noise).
 */
export const stashAxiosErrorContext = async (error: AxiosError): Promise<void> => {
  if (!env.sentryDsn) return;

  try {
    const { response, config } = error;
    if (response?.status === 401) return;

    axiosErrorContext.set(error, {
      method: config?.method?.toUpperCase(),
      path: config?.url?.split("?")[0],
      status: response?.status,
      statusText: response?.statusText,
      requestId: getRequestId(response?.headers),
      body: response ? await extractBody(response.data) : undefined,
    });
  } catch {
    // Enriching Sentry must never mask the original request failure.
  }
};

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PUBLIC_ID_PATTERN = /^[a-z]{2,5}_[A-Za-z0-9]+$/;

// Collapse id-bearing path segments so `/users/usr_123` and `/users/usr_456`
// fingerprint to the same `/users/:id` issue instead of one issue per record.
const normalizePath = (path: string | undefined): string => {
  if (!path) return "unknown";
  return path
    .split("/")
    .map((segment) =>
      /^\d+$/.test(segment) || UUID_PATTERN.test(segment) || PUBLIC_ID_PATTERN.test(segment)
        ? ":id"
        : segment
    )
    .join("/");
};

/**
 * `beforeSend` hook: enriches axios errors with the response context stashed by
 * the interceptor, gives the issue a readable title, and fingerprints by
 * endpoint + status. Returns non-axios events untouched.
 */
export const enrichSentryEventWithAxios = (event: ErrorEvent, hint: EventHint): ErrorEvent => {
  const error = hint?.originalException;
  if (!Axios.isAxiosError(error)) return event;

  // Prefer the interceptor's stash (has the parsed/blob body); fall back to
  // reading the error synchronously for axios errors from other code paths.
  const context: AxiosErrorContext = axiosErrorContext.get(error) ?? {
    method: error.config?.method?.toUpperCase(),
    path: error.config?.url?.split("?")[0],
    status: error.response?.status,
    statusText: error.response?.statusText,
    requestId: getRequestId(error.response?.headers),
  };

  const normalizedPath = normalizePath(context.path);

  event.contexts = {
    ...event.contexts,
    response: {
      method: context.method,
      url: context.path,
      status: context.status,
      status_text: context.statusText,
      request_id: context.requestId,
      body: context.body,
    },
  };

  const exception = event.exception?.values?.[0];
  if (exception && context.status) {
    exception.value = `${context.method ?? "REQUEST"} ${normalizedPath} → ${context.status}${
      context.statusText ? ` ${context.statusText}` : ""
    }`;
  }

  event.fingerprint = [
    "axios",
    context.method ?? "unknown",
    normalizedPath,
    String(context.status ?? "unknown"),
  ];

  return event;
};
